import { useRef, useEffect } from "react";
import {
  Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered,
  Quote, Link2, Image as ImageIcon, Table as TableIcon, Code, AlignLeft, MessageSquareQuote,
} from "lucide-react";
import { toast } from "sonner";
import { api, getAdminToken } from "../lib/api";

function Btn({ onClick, title, children }) {
  return (
    <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange }) {
  const ref = useRef(null);
  const imageInputRef = useRef(null);
  const savedRange = useRef(null);

  useEffect(() => {
    if (ref.current && value !== ref.current.innerHTML) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line
  }, []);

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (command, val = null) => { document.execCommand(command, false, val); ref.current.focus(); emit(); };
  const format = (tag) => cmd("formatBlock", tag);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRange.current = null;
    }
  };

  const restoreSelection = () => {
    ref.current.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const insertHTML = (html) => {
    ref.current.focus();
    document.execCommand("insertHTML", false, html);
    emit();
  };

  const addLink = () => {
    let url = prompt("Enter link URL:\n• Internal page: /tools/numerology-calculator\n• External: https://example.com");
    if (!url) return;
    url = url.trim();
    if (!url.startsWith("/") && !url.startsWith("#") && !url.startsWith("mailto:") && !/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }
    cmd("createLink", url);
  };

  const openImagePicker = () => {
    saveSelection(); // remember where the cursor is before the picker steals focus
    imageInputRef.current?.click();
  };

  const onImageFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${getAdminToken()}` },
      });
      const url = process.env.REACT_APP_BACKEND_URL + data.url;
      restoreSelection(); // drop the image where the cursor was
      document.execCommand("insertHTML", false, `<img src="${url}" alt="" /><p><br/></p>`);
      emit();
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    }
  };

  const addTable = () => insertHTML(
    `<table><thead><tr><th>Heading 1</th><th>Heading 2</th></tr></thead><tbody><tr><td>Cell</td><td>Cell</td></tr><tr><td>Cell</td><td>Cell</td></tr></tbody></table><p><br/></p>`
  );
  const addCallout = () => insertHTML(`<div class="callout"><strong>💡 Tip:</strong> Add your insight here.</div><p><br/></p>`);
  const addCode = () => insertHTML(`<pre><code>// your code here</code></pre><p><br/></p>`);

  return (
    <div className="rounded-2xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-2" data-testid="editor-toolbar">
        <Btn title="Bold" onClick={() => cmd("bold")}><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" onClick={() => cmd("italic")}><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" onClick={() => cmd("underline")}><Underline className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn title="Heading 2" onClick={() => format("h2")}><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Heading 3" onClick={() => format("h3")}><Heading3 className="h-4 w-4" /></Btn>
        <Btn title="Paragraph" onClick={() => format("p")}><AlignLeft className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn title="Bullet list" onClick={() => cmd("insertUnorderedList")}><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" onClick={() => cmd("insertOrderedList")}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Quote" onClick={() => format("blockquote")}><Quote className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn title="Link" onClick={addLink}><Link2 className="h-4 w-4" /></Btn>
        <Btn title="Image" onClick={openImagePicker}><ImageIcon className="h-4 w-4" /></Btn>
        <Btn title="Table" onClick={addTable}><TableIcon className="h-4 w-4" /></Btn>
        <Btn title="Callout box" onClick={addCallout}><MessageSquareQuote className="h-4 w-4" /></Btn>
        <Btn title="Code block" onClick={addCode}><Code className="h-4 w-4" /></Btn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        data-testid="editor-content"
        className="prose-numoraa min-h-[420px] max-w-none p-6 outline-none"
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onImageFile}
        data-testid="editor-image-upload"
      />
    </div>
  );
}
